import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { docusignApi, extractErrorMessage } from '../../../services/hrApi';
import ConfirmationModal from '../../../components/ConfirmationModal';
import './StepStyles.scss';

interface SignatureVerificationStepProps {
  employeeId?: string;
  data?: any;
  onDataChange: (data: any) => void;
  onComplete: (isComplete: boolean) => void;
}

const SignatureVerificationStep: React.FC<SignatureVerificationStepProps> = ({
  employeeId,
  data,
  onDataChange,
  onComplete,
}) => {
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [envelopeStatus, setEnvelopeStatus] = useState<any>(null);
  const [processingAction, setProcessingAction] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);

  useEffect(() => {
    if (employeeId) {
      checkSignatureStatus();
    } else {
      setCheckingStatus(false);
    }
  }, [employeeId]);

  const checkSignatureStatus = async (isManualRefresh: boolean = false) => {
    if (!employeeId) {
      setCheckingStatus(false);
      return;
    }
    
    setCheckingStatus(true);
    try {
      const result = await docusignApi.checkSignatureStatus(employeeId, 'employee-offboarding');
      
      if (result.success) {
        setEnvelopeStatus(result);
        if (isManualRefresh) {
          toast.success('Signature status refreshed successfully');
        }
      } else {
        console.error('Failed to check signature status:', result.error);
        toast.error(`Failed to check signature status: ${result.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Error checking signature status:', error);
      const errorMessage = extractErrorMessage(error);
      toast.error(`Failed to check signature status: ${errorMessage}`);
    } finally {
      setCheckingStatus(false);
    }
  };

  const getDisplayStatus = (status: string): string => {
    if (status === 'voided') return 'Voided';
    if (status === 'sent') return 'Pending';
    if (status === 'completed') return 'Completed';
    return 'In Draft';
  };

  const getStatusColor = (status: string): string => {
    if (status === 'voided') return '#dc3545';
    if (status === 'sent') return '#ffc107';
    if (status === 'completed') return '#28a745';
    return '#6c757d';
  };

  const getStatusConfig = (status: string) => {
    if (status === 'completed') {
      return {
        icon: 'bx bx-check-circle',
        iconBackground: '#e8f5e9',
        iconShadow: 'rgba(40, 167, 69, 0.2)',
        description: 'All documents have been signed and completed successfully.'
      };
    } else if (status === 'sent') {
      return {
        icon: 'bx bx-time-five',
        iconBackground: '#fff3cd',
        iconShadow: 'rgba(255, 193, 7, 0.2)',
        description: 'Documents have been sent and are awaiting signature from the recipient.'
      };
    } else if (status === 'voided') {
      return {
        icon: 'bx bx-x-circle',
        iconBackground: '#f8d7da',
        iconShadow: 'rgba(220, 53, 69, 0.2)',
        description: 'This envelope has been voided. You may need to create a new one.'
      };
    } else {
      return {
        icon: 'bx bx-file-blank',
        iconBackground: '#e9ecef',
        iconShadow: 'rgba(108, 117, 125, 0.2)',
        description: 'Envelope is in draft status. Go to Document Package step to place signature tabs and send.'
      };
    }
  };

  useEffect(() => {
    if (envelopeStatus && envelopeStatus.has_envelope && envelopeStatus.envelopes) {
      const nonVoidedEnvelopes = envelopeStatus.envelopes.filter((env: any) => env.status !== 'voided');
      const allCompleted = nonVoidedEnvelopes.length > 0 && nonVoidedEnvelopes.every((env: any) => env.status === 'completed');
      onComplete(allCompleted);
    } else {
      onComplete(false);
    }
  }, [envelopeStatus, onComplete]);

  const getActiveEnvelope = () => {
    if (!envelopeStatus || !envelopeStatus.has_envelope || !envelopeStatus.envelopes) {
      return null;
    }
    return envelopeStatus.envelopes.find((env: any) => env.status !== 'voided') || null;
  };

  const handleResendEnvelope = async () => {
    setShowResendModal(false);
    const activeEnvelope = getActiveEnvelope();
    if (!activeEnvelope || !activeEnvelope.envelope_id) {
      toast.error('No envelope found.');
      return;
    }

    if (activeEnvelope.status !== 'sent') {
      toast.error('Can only resend envelopes that have been sent.');
      return;
    }

    setProcessingAction(true);
    try {
      const result = await docusignApi.resendEnvelope(activeEnvelope.envelope_id);

      if (result.success) {
        toast.success('Envelope resent successfully to all pending recipients.');
        await checkSignatureStatus();
      } else {
        toast.error(`Failed to resend envelope: ${result.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Error resending envelope:', error);
      const errorMessage = extractErrorMessage(error);
      toast.error(`Failed to resend envelope: ${errorMessage}`);
    } finally {
      setProcessingAction(false);
    }
  };

  const handleVoidEnvelope = async () => {
    setShowVoidModal(false);
    const activeEnvelope = getActiveEnvelope();
    if (!activeEnvelope || !activeEnvelope.envelope_id) {
      toast.error('No envelope found.');
      return;
    }

    if (activeEnvelope.status === 'voided') {
      toast.error('Envelope is already voided.');
      return;
    }

    setProcessingAction(true);
    try {
      const result = await docusignApi.voidEnvelope(activeEnvelope.envelope_id);

      if (result.success) {
        onComplete(false);
        await checkSignatureStatus();
        toast.success('Envelope voided successfully. Please go back to the Document Package step to create a new envelope.');
      } else {
        toast.error(`Failed to void envelope: ${result.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Error voiding envelope:', error);
      const errorMessage = extractErrorMessage(error);
      toast.error(`Failed to void envelope: ${errorMessage}`);
    } finally {
      setProcessingAction(false);
    }
  };

  return (
    <div className="wizard-step">
      <div className="step-header">
        <h4>Signature Verification</h4>
        <p>Track signed documents and verify signatures</p>
        {!checkingStatus && employeeId && (
          <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => checkSignatureStatus(true)}
              disabled={checkingStatus || processingAction}
              style={{
                padding: '8px 16px',
                background: '#0dcaf0',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: (checkingStatus || processingAction) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                opacity: (checkingStatus || processingAction) ? 0.6 : 1
              }}
            >
              {checkingStatus ? (
                <>
                  <i className="bx bx-loader-alt bx-spin"></i>
                  Checking...
                </>
              ) : (
                <>
                  <i className="bx bx-refresh"></i>
                  Check Again
                </>
              )}
            </button>
          </div>
        )}
      </div>

      <div className="step-form">
        {checkingStatus && (
          <div style={{ 
            marginBottom: '20px', 
            textAlign: 'center', 
            padding: '60px 40px',
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            borderRadius: '12px',
            border: '1px solid #e9ecef'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 20px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #0dcaf0 0%, #0aa2c0 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(13, 202, 240, 0.3)'
            }}>
              <i className="bx bx-loader-alt bx-spin" style={{ fontSize: '32px', color: '#ffffff' }}></i>
            </div>
            <p style={{ margin: 0, fontSize: '16px', color: '#495057', fontWeight: 500 }}>
              Checking envelope status...
            </p>
          </div>
        )}

        {!checkingStatus && envelopeStatus && envelopeStatus.has_envelope && envelopeStatus.envelopes && (() => {
          const nonVoidedEnvelopes = envelopeStatus.envelopes.filter((env: any) => env.status !== 'voided');
          
          if (nonVoidedEnvelopes.length === 0) {
            return (
              <div style={{
                marginBottom: '20px',
                padding: '48px 32px',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '96px',
                  height: '96px',
                  margin: '0 auto 24px',
                  borderRadius: '50%',
                  background: '#e9ecef',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 24px rgba(108, 117, 125, 0.2)'
                }}>
                  <i className="bx bx-envelope" style={{ fontSize: '48px', color: '#6c757d' }}></i>
                </div>
                <h3 style={{
                  margin: '0 0 12px 0',
                  fontSize: '20px',
                  fontWeight: 600,
                  color: '#495057',
                }}>
                  No Active Envelopes
                </h3>
                <p style={{
                  margin: '0 auto',
                  fontSize: '14px',
                  color: '#6c757d',
                  lineHeight: '1.6',
                  maxWidth: '400px'
                }}>
                  Create an envelope in the Document Package step to see signature status here.
                </p>
              </div>
            );
          }
          
          return (
            <div style={{ marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {nonVoidedEnvelopes.map((env: any, index: number) => {
                const displayStatus = getDisplayStatus(env.status);
                const statusColor = getStatusColor(env.status);
                const statusConfig = getStatusConfig(env.status);
                
                return (
                  <div
                    key={index}
                    style={{
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '32px 0'
                    }}
                  >
                    <div
                      style={{
                        width: '96px',
                        height: '96px',
                        borderRadius: '50%',
                        background: statusConfig.iconBackground,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '24px',
                        boxShadow: `0 8px 24px ${statusConfig.iconShadow}`
                      }}
                    >
                      <i 
                        className={statusConfig.icon} 
                        style={{ 
                          fontSize: '48px', 
                          color: statusColor 
                        }}
                      ></i>
                    </div>
                    
                    <h3 style={{
                      margin: '0 0 12px 0',
                      fontSize: '20px',
                      fontWeight: 600,
                      color: statusColor,
                    }}>
                      {displayStatus}
                    </h3>
                    
                    <p style={{
                      margin: 0,
                      fontSize: '14px',
                      color: '#6c757d',
                      lineHeight: '1.6',
                      maxWidth: '400px',
                      textAlign: 'center'
                    }}>
                      {statusConfig.description}
                    </p>

                    {env.status === 'sent' && (
                      <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'center' }}>
                        <button
                          type="button"
                          onClick={() => setShowResendModal(true)}
                          disabled={processingAction}
                          style={{
                            padding: '10px 20px',
                            background: '#0dcaf0',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: processingAction ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            opacity: processingAction ? 0.6 : 1
                          }}
                        >
                          <i className="bx bx-refresh"></i>
                          Resend
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowVoidModal(true)}
                          disabled={processingAction}
                          style={{
                            padding: '10px 20px',
                            background: '#dc3545',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: processingAction ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            opacity: processingAction ? 0.6 : 1
                          }}
                        >
                          <i className="bx bx-x"></i>
                          Void
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {!checkingStatus && (!envelopeStatus || !envelopeStatus.has_envelope) && (
          <div style={{
            marginBottom: '20px',
            padding: '48px 32px',
            textAlign: 'center'
          }}>
            <div style={{
              width: '96px',
              height: '96px',
              margin: '0 auto 24px',
              borderRadius: '50%',
              background: '#e9ecef',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <i className="bx bx-envelope" style={{ fontSize: '48px', color: '#6c757d' }}></i>
            </div>
            <h3 style={{
              margin: '0 0 12px 0',
              fontSize: '20px',
              fontWeight: 600,
              color: '#495057',
            }}>
              No Envelope Found
            </h3>
            <p style={{
              margin: '0 auto',
              fontSize: '14px',
              color: '#6c757d',
              lineHeight: '1.6',
              maxWidth: '400px'
            }}>
              Create an envelope in the Document Package step to see signature status here.
            </p>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showResendModal}
        title="Resend Envelope"
        message="Are you sure you want to resend this envelope? All pending recipients will receive a new notification email."
        confirmText="Resend"
        cancelText="Cancel"
        variant="info"
        onConfirm={handleResendEnvelope}
        onCancel={() => setShowResendModal(false)}
        isLoading={processingAction}
      />

      <ConfirmationModal
        isOpen={showVoidModal}
        title="Void Envelope"
        message="Are you sure you want to void this envelope? This action cannot be undone. You will need to create a new envelope in the Document Package step."
        confirmText="Void Envelope"
        cancelText="Cancel"
        variant="danger"
        icon="bx bx-x-circle"
        onConfirm={handleVoidEnvelope}
        onCancel={() => setShowVoidModal(false)}
        isLoading={processingAction}
      />
    </div>
  );
};

export default SignatureVerificationStep;

